import { NavLink, useLocation } from "react-router-dom";
import {
  BarChart3,
  Megaphone,
  MessageCircle,
  Users,
  Settings,
  PenTool,
  MessageSquare,
  Send,
  Radar,
  ChevronDown,
  CreditCard,
  FileText,
  ShieldCheck,
  Bot,
  PhoneForwarded,
  Image as ImageIcon,
  Zap,
  Receipt,
  FolderOpen,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Separator } from "@/components/ui/separator";

const mainItems = [
  { 
    title: "Dashboard", 
    url: "/", 
    icon: BarChart3,
    description: "Visão geral e métricas" 
  },
  // { 
  //   title: "Campanhas", 
  //   url: "/campanhas", 
  //   icon: Megaphone,
  //   description: "Campanhas WhatsApp" 
  // }, // TEMPORARIAMENTE OCULTO
  { 
    title: "Conversas", 
    url: "/conversas", 
    icon: MessageCircle,
    description: "Inbox centralizado" 
  },
  { 
    title: "Contatos", 
    url: "/contatos", 
    icon: Users,
    description: "Base de produtores" 
  },
  { 
    title: "Carteiras", 
    url: "/carteiras", 
    icon: FolderOpen,
    description: "Carteiras de clientes" 
  },
  // { 
  //   title: "Billing", 
  //   url: "/billing", 
  //   icon: CreditCard,
  //   description: "Planos e assinaturas" 
  // }, // TEMPORARIAMENTE OCULTO
];

const toolItems = [
  { 
    title: "Templates", 
    url: "/templates", 
    icon: MessageSquare,
    description: "Modelos de mensagem" 
  },
  // { 
  //   title: "Assinaturas", 
  //   url: "/assinaturas", 
  //   icon: PenTool,
  //   description: "Documentos eletrônicos" 
  // }, // TEMPORARIAMENTE OCULTO
  { 
    title: "Disparos", 
    url: "/disparos", 
    icon: Send,
    description: "Envios manuais" 
  },
  { 
    title: "Follow-up de Boletos", 
    url: "/follow-up-boletos", 
    icon: PhoneForwarded,
    description: "Cobranças já enviadas" 
  },
  { 
    title: "Automações", 
    url: "/automacoes", 
    icon: Zap,
    description: "Réguas de cobrança" 
  },
  { 
    title: "Base de Boletos", 
    url: "/boletos", 
    icon: Receipt,
    description: "Gestão de duplicatas" 
  },
  {
    title: "Robô Conversacional",
    url: "/robo-conversacional",
    icon: Bot,
    description: "Chatbot WhatsApp"
  },
  // { 
  //   title: "Minhas CPRs",
  //   url: "/cprs", 
  //   icon: FileText,
  //   description: "Gestão de CPRs" 
  // }, // TEMPORARIAMENTE OCULTO
  { 
    title: "Análise de Garantias", 
    url: "/garantias", 
    icon: ShieldCheck,
    description: "Proc. de matrículas" 
  },
  { 
    title: "Central de Canhotos", 
    url: "/canhotos", 
    icon: ImageIcon,
    description: "Envio inteligente" 
  },
  // { 
  //   title: "Radar Agro", 
  //   url: "/radar-agro", 
  //   icon: Radar,
  //   description: "Alertas e cotações" 
  // }, // TEMPORARIAMENTE OCULTO
  { 
    title: "Configurações", 
    url: "/configuracoes", 
    icon: Settings,
    description: "Integrações" 
  },
];

export function AppSidebar() {
  const { state } = useSidebar();
  const location = useLocation();

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const getNavClassName = (path: string) => {
    const active = isActive(path);
    return active 
      ? "relative bg-gradient-to-r from-[#22C55E] to-[#16A34A] text-white font-semibold shadow-[0_1px_5px_rgba(34,197,94,0.3)] border-l-[3px] border-[#34D399] transition-all duration-300 rounded-[10px]"
      : "text-[#1F2937] hover:bg-[#D1FAE5]/50 hover:text-[#16A34A] transition-all duration-250 rounded-[10px]";
  };

  const collapsed = state === "collapsed";

  return (
    <Sidebar className={`border-r border-[#E5E7EB] bg-white/80 backdrop-blur-md shadow-[0_2px_10px_rgba(0,0,0,0.05)] ${collapsed ? 'w-16' : 'w-72'}`}>
      <SidebarContent className="overflow-y-auto scrollbar-none">
        <div className="px-5 py-6">
          {!collapsed ? (
            <Accordion type="multiple" defaultValue={["main", "tools"]} className="space-y-3">
              {/* Main Navigation Section */}
              <AccordionItem value="main" className="border-none">
                <AccordionTrigger className="text-[0.7rem] font-semibold text-[#6B7280]/60 uppercase tracking-[0.08em] hover:no-underline py-3 px-0 hover:text-[#6B7280]">
                  <span>Principal</span>
                </AccordionTrigger>
                <div className="h-px bg-[#E5E7EB]/40 my-2"></div>
                <AccordionContent className="pb-3 pt-2">
                  <SidebarMenu className="space-y-2">
                    {mainItems.map((item, index) => (
                      <SidebarMenuItem 
                        key={item.title}
                        className="animate-fade-in"
                        style={{ 
                          animationDelay: `${index * 40}ms`,
                          animationFillMode: 'both'
                        }}
                      >
                        <SidebarMenuButton asChild className="w-full group">
                          <NavLink to={item.url} className={`${getNavClassName(item.url)} px-4 py-2.5 flex items-center gap-2.5`}>
                            <item.icon className={`h-5 w-5 flex-shrink-0 transition-all duration-300 ${isActive(item.url) ? 'text-white' : 'text-[#9CA3AF] group-hover:text-[#16A34A] group-hover:scale-110'}`} />
                            <div className="flex flex-col items-start min-w-0 flex-1 overflow-hidden">
                              <span className={`text-sm font-medium leading-tight truncate w-full ${isActive(item.url) ? 'text-white' : 'text-[#1F2937]'}`}>{item.title}</span>
                              <span className={`text-xs leading-tight mt-0.5 truncate w-full ${isActive(item.url) ? 'text-white/80' : 'text-[#6B7280]'}`}>{item.description}</span>
                            </div>
                          </NavLink>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </AccordionContent>
              </AccordionItem>

              <Separator className="my-6 bg-[#E5E7EB]/40" />

              {/* Tools Navigation Section */}
              <AccordionItem value="tools" className="border-none">
                <AccordionTrigger className="text-[0.7rem] font-semibold text-[#6B7280]/60 uppercase tracking-[0.08em] hover:no-underline py-3 px-0 hover:text-[#6B7280]">
                  <span>Ferramentas</span>
                </AccordionTrigger>
                <div className="h-px bg-[#E5E7EB]/40 my-2"></div>
                <AccordionContent className="pb-3 pt-2">
                  <SidebarMenu className="space-y-2">
                    {toolItems.map((item, index) => (
                      <SidebarMenuItem 
                        key={item.title}
                        className="animate-fade-in"
                        style={{ 
                          animationDelay: `${(mainItems.length + index) * 40}ms`,
                          animationFillMode: 'both'
                        }}
                      >
                        <SidebarMenuButton asChild className="w-full group">
                          <NavLink to={item.url} className={`${getNavClassName(item.url)} px-4 py-2.5 flex items-center gap-2.5`}>
                            <item.icon className={`h-5 w-5 flex-shrink-0 transition-all duration-300 ${isActive(item.url) ? 'text-white' : 'text-[#9CA3AF] group-hover:text-[#16A34A] group-hover:scale-110'}`} />
                            <div className="flex flex-col items-start min-w-0 flex-1 overflow-hidden">
                              <span className={`text-sm font-medium leading-tight truncate w-full ${isActive(item.url) ? 'text-white' : 'text-[#1F2937]'}`}>{item.title}</span>
                              <span className={`text-xs leading-tight mt-0.5 truncate w-full ${isActive(item.url) ? 'text-white/80' : 'text-[#6B7280]'}`}>{item.description}</span>
                            </div>
                          </NavLink>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          ) : (
            // Collapsed sidebar - show only icons
            <div className="space-y-6">
              <SidebarGroup>
                <SidebarGroupContent>
                  <SidebarMenu className="space-y-2">
                    {mainItems.map((item, index) => (
                      <SidebarMenuItem 
                        key={item.title}
                        className="animate-fade-in"
                        style={{ 
                          animationDelay: `${index * 40}ms`,
                          animationFillMode: 'both'
                        }}
                      >
                        <SidebarMenuButton asChild className="w-full group">
                          <NavLink to={item.url} className={`${getNavClassName(item.url)} px-3 py-3 flex items-center justify-center`}>
                            <item.icon className={`h-5 w-5 transition-all duration-300 ${isActive(item.url) ? 'text-white' : 'text-[#9CA3AF] group-hover:text-[#16A34A] group-hover:scale-110'}`} />
                          </NavLink>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>

              <Separator className="bg-[#E5E7EB]/40" />

              <SidebarGroup>
                <SidebarGroupContent>
                  <SidebarMenu className="space-y-2">
                    {toolItems.map((item, index) => (
                      <SidebarMenuItem 
                        key={item.title}
                        className="animate-fade-in"
                        style={{ 
                          animationDelay: `${(mainItems.length + index) * 40}ms`,
                          animationFillMode: 'both'
                        }}
                      >
                        <SidebarMenuButton asChild className="w-full group">
                          <NavLink to={item.url} className={`${getNavClassName(item.url)} px-3 py-3 flex items-center justify-center`}>
                            <item.icon className={`h-5 w-5 transition-all duration-300 ${isActive(item.url) ? 'text-white' : 'text-[#9CA3AF] group-hover:text-[#16A34A] group-hover:scale-110'}`} />
                          </NavLink>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </div>
          )}
          
          {/* Bottom spacing */}
          <div className="h-12"></div>
        </div>
      </SidebarContent>
    </Sidebar>
  );
}